import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Collaboration } from '../../types';
import { apiService } from '../../services/api';

interface CollaborationState {
  collaborations: Collaboration[];
  myInvitations: Collaboration[];
  sharedRoutes: any[];
  loading: boolean;
  error: string | null;
}

const initialState: CollaborationState = {
  collaborations: [],
  myInvitations: [],
  sharedRoutes: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchCollaborations = createAsyncThunk(
  'collaboration/fetchCollaborations',
  async (routeId: number) => {
    return await apiService.getCollaborations(routeId);
  }
);

export const createCollaboration = createAsyncThunk(
  'collaboration/createCollaboration',
  async ({ routeId, data }: { routeId: number; data: Partial<Collaboration> }) => {
    return await apiService.createCollaboration(routeId, data);
  }
);

export const deleteCollaboration = createAsyncThunk(
  'collaboration/deleteCollaboration',
  async (id: number) => {
    await apiService.deleteCollaboration(id);
    return id;
  }
);

export const fetchMyInvitations = createAsyncThunk(
  'collaboration/fetchMyInvitations',
  async () => {
    return await apiService.getMyInvitations();
  }
);

export const acceptInvitation = createAsyncThunk(
  'collaboration/acceptInvitation',
  async (id: number) => {
    return await apiService.acceptInvitation(id);
  }
);

export const fetchSharedRoutes = createAsyncThunk(
  'collaboration/fetchSharedRoutes',
  async () => {
    return await apiService.getSharedRoutes();
  }
);

const collaborationSlice = createSlice({
  name: 'collaboration',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch collaborations
      .addCase(fetchCollaborations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollaborations.fulfilled, (state, action) => {
        state.loading = false;
        state.collaborations = action.payload;
      })
      .addCase(fetchCollaborations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Ошибка загрузки совместной работы';
      })
      // Create collaboration
      .addCase(createCollaboration.fulfilled, (state, action) => {
        state.collaborations.push(action.payload);
      })
      // Delete collaboration
      .addCase(deleteCollaboration.fulfilled, (state, action) => {
        state.collaborations = state.collaborations.filter(collab => collab.id !== action.payload);
      })
      // Fetch my invitations
      .addCase(fetchMyInvitations.fulfilled, (state, action) => {
        state.myInvitations = action.payload;
      })
      // Accept invitation
      .addCase(acceptInvitation.fulfilled, (state, action) => {
        const index = state.myInvitations.findIndex(inv => inv.id === action.payload.id);
        if (index !== -1) {
          state.myInvitations[index] = action.payload;
        }
      })
      // Fetch shared routes
      .addCase(fetchSharedRoutes.fulfilled, (state, action) => {
        state.sharedRoutes = action.payload;
      });
  },
});

export const { clearError } = collaborationSlice.actions;
export default collaborationSlice.reducer; 